import { useState } from 'react'
import { useSnackbar } from '../../../context/snackbar-context'
import {
  Section,
  SectionTitle,
  SectionSubtitle,
  Row,
  RowText,
  RowLabel,
  RowDescription,
} from '../../Profile/profile-styles'
import {
  MovieceptionSwitch,
  MovieceptionOutlinedButton,
} from '../../../components/Buttons/movieception-buttons'
import { ConfirmModal } from '../../../utils/confirm-modal'
import { SettingsResponse } from '../../../client/settings-api'
import { useBrowseHistory } from '../../../context/browse-history-context'

interface Props {
  settings: SettingsResponse | null
  isDisabled: boolean
  onUpdate: (payload: Partial<SettingsResponse>) => void
  setSettings: React.Dispatch<React.SetStateAction<SettingsResponse | null>>
}

export const PrivacySection = ({
  settings,
  isDisabled,
  onUpdate,
  setSettings,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar()
  // Via the shared provider so the drawer, search recents, and profile tab
  // all empty out immediately.
  const { clearViews } = useBrowseHistory()
  const [clearOpen, setClearOpen] = useState(false)
  const [clearing, setClearing] = useState(false)

  const handleClearBrowseHistory = async () => {
    setClearing(true)
    try {
      await clearViews()
      enqueueSnackbar('Browse history cleared', { variant: 'success' })
    } catch {
      enqueueSnackbar('Could not clear browse history. Please try again.', {
        variant: 'error',
      })
    } finally {
      setClearing(false)
      setClearOpen(false)
    }
  }

  return (
    <>
      <Section>
        <SectionTitle>Privacy</SectionTitle>
        <SectionSubtitle>Control your privacy settings.</SectionSubtitle>

        <Row>
          <RowText>
            <RowLabel>Public Profile</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={(settings?.profileVisibility ?? 'public') === 'public'}
            onChange={(e) => {
              if (!settings) return
              const value = e.target.checked ? 'public' : 'private'
              setSettings({ ...settings, profileVisibility: value })
              onUpdate({ profileVisibility: value })
            }}
            disabled={isDisabled}
          />
        </Row>

        <Row>
          <RowText>
            <RowLabel>Keep Logged In For 30 Days</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.keepLoggedIn ?? false}
            onChange={(e) => {
              if (!settings) return
              const value = e.target.checked
              setSettings({ ...settings, keepLoggedIn: value })
              onUpdate({ keepLoggedIn: value })
            }}
            disabled={isDisabled}
          />
        </Row>

        <Row>
          <RowText>
            <RowLabel>Browse History</RowLabel>
            <RowDescription>
              Remember titles you open to power "Recently viewed" and better
              recommendations. Only you can see this.
            </RowDescription>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.browseHistoryEnabled ?? true}
            onChange={(e) => {
              if (!settings) return
              const checked = e.target.checked
              setSettings({ ...settings, browseHistoryEnabled: checked })
              onUpdate({ browseHistoryEnabled: checked })
            }}
            disabled={isDisabled}
          />
        </Row>

        <Row>
          <RowText>
            <RowLabel>Clear Browse History</RowLabel>
          </RowText>
          <MovieceptionOutlinedButton
            onClick={() => setClearOpen(true)}
            disabled={isDisabled}
            label='Clear'
          />
        </Row>
      </Section>

      <ConfirmModal
        open={clearOpen}
        title='Clear Browse History'
        description='This removes every recently viewed title from your browse history.'
        warning="This can't be undone."
        confirmText={clearing ? 'Clearing…' : 'Clear History'}
        danger
        confirmDisabled={clearing}
        onConfirm={handleClearBrowseHistory}
        onCancel={() => setClearOpen(false)}
      />
    </>
  )
}
